import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Loader2 } from "lucide-react";

/**
 * Edit an existing marketing lead. Admin, Marketing Manager and Sales Manager can call PATCH /leads/{id}.
 *
 * Sales-Manager picker: intentionally UNFILTERED across branches (matches admin request Feb 2026)
 * so leads can be reassigned to any manager. Each option shows the manager's branch in parens
 * for disambiguation, e.g. "John Doe (Sarjapura)".
 */
export default function EditLeadDialog({ open, lead, onClose, onSaved }) {
  const [branches, setBranches] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    client_name: "", phone: "", email: "", location: "", source: "", remarks: "",
    project_name: "", branch_id: "", assigned_sales_manager_id: "",
  });

  useEffect(() => {
    if (!open || !lead) return;
    setErr("");
    setForm({
      client_name: lead.client_name || "",
      phone: lead.phone || "",
      email: lead.email || "",
      location: lead.location || "",
      source: lead.source || "",
      remarks: lead.remarks || "",
      project_name: lead.project_name || "",
      branch_id: lead.branch_id || "",
      assigned_sales_manager_id: lead.assigned_sales_manager_id || "",
    });
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [open, lead]);

  const loadSalesManagers = useCallback(async () => {
    try {
      // Unfiltered — show every active Sales Manager across all branches.
      const { data } = await api.get(`/users/sales-managers`);
      setSalesManagers(data);
    } catch (_e) {
      setSalesManagers([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadSalesManagers();
  }, [open, loadSalesManagers]);

  const smOptions = useMemo(
    () => salesManagers.map((m) => ({
      id: m.id,
      label: `${m.name} (${m.branch_name || "Global"})`,
    })),
    [salesManagers]
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.client_name.trim() || !form.phone.trim()) {
      setErr("Client name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        location: form.location.trim() || null,
        source: form.source.trim() || null,
        remarks: form.remarks.trim() || null,
        project_name: form.project_name.trim() || null,
      };
      if (form.branch_id) payload.branch_id = form.branch_id;
      else payload.clear_branch = true;
      if (form.assigned_sales_manager_id) payload.assigned_sales_manager_id = form.assigned_sales_manager_id;
      else payload.clear_sales_manager = true;
      const { data } = await api.patch(`/leads/${lead.id}`, payload);
      toast.success("Lead updated");
      onSaved?.(data);
      onClose();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !lead) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="edit-lead-dialog">
      <div className="w-full max-w-lg iz-card p-8 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500" data-testid="edit-lead-close">
          <X className="w-4 h-4" />
        </button>
        <div className="pb-4 border-b border-[#262626] mb-5">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Refine</p>
          <h2 className="font-display text-xl text-white">Edit lead</h2>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Client Name</label>
            <input value={form.client_name} onChange={(e) => setForm(f => ({...f, client_name: e.target.value}))} className="iz-input" required data-testid="edit-lead-name" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Project Name</label>
            <input value={form.project_name} onChange={(e) => setForm(f => ({...f, project_name: e.target.value}))} className="iz-input" data-testid="edit-lead-project-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Phone</label>
              <input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} className="iz-input" required data-testid="edit-lead-phone" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} className="iz-input" data-testid="edit-lead-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Location</label>
              <input value={form.location} onChange={(e) => setForm(f => ({...f, location: e.target.value}))} className="iz-input" data-testid="edit-lead-location" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Source</label>
              <input value={form.source} onChange={(e) => setForm(f => ({...f, source: e.target.value}))} className="iz-input" data-testid="edit-lead-source" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Branch</label>
              <select value={form.branch_id} onChange={(e) => setForm(f => ({...f, branch_id: e.target.value}))} className="iz-input" data-testid="edit-lead-branch">
                <option value="">— None —</option>
                {branches.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
                Assign Sales Manager <span className="lowercase text-gray-600">(any branch)</span>
              </label>
              <select value={form.assigned_sales_manager_id} onChange={(e) => setForm(f => ({...f, assigned_sales_manager_id: e.target.value}))} className="iz-input" data-testid="edit-lead-sales-manager">
                <option value="">— None —</option>
                {smOptions.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Remarks</label>
            <textarea value={form.remarks} onChange={(e) => setForm(f => ({...f, remarks: e.target.value}))} className="iz-input" rows={3} data-testid="edit-lead-remarks" />
          </div>

          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary" data-testid="edit-lead-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary flex items-center gap-2" data-testid="edit-lead-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
